import { useState, useEffect } from 'react';

// Optional: How long data should be kept (in days). Set to null to never expire.
const EXPIRY_DAYS = null;

/**
 * CRM Filter Persistence Hook
 * 
 * Domain Logic:
 * - Persists search terms and filter selections across browser sessions
 * - Supports configurable data expiration for CRM filter cleanup
 * - Handles localStorage errors gracefully with fallback values
 * - Automatically syncs filter state when storage key changes
 * - Provides filter clearing functionality for user convenience
 * 
 * @param {string} storageKey - Unique key for filter persistence
 * @param {string} [defaultSearch] - Default search term
 * @param {Array} [defaultFilters] - Default filter selections
 * @returns {Object} Persistent filter state and management functions
 */

/**
 * Checks if stored data has expired based on timestamp
 * Returns false if no expiry is configured or no timestamp exists
 */
function isExpired(timestamp) {
  if (!EXPIRY_DAYS || !timestamp) return false;
  const ageMs = Date.now() - timestamp;
  return ageMs > EXPIRY_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Loads item from localStorage with expiration check
 * Returns fallback value if data is expired or invalid
 */
function loadItem(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return isExpired(parsed.timestamp) ? fallback : parsed.value;
  } catch {
    return fallback;
  }
}

/**
 * Saves item to localStorage with timestamp for expiration tracking
 */
function saveItem(key, value) {
  const payload = {
    value,
    timestamp: Date.now(),
  };
  localStorage.setItem(key, JSON.stringify(payload));
}

export default function usePersistentFilters(storageKey, defaultSearch = '', defaultFilters = []) {
  // State for persistent filter management
  /** @type {string} Current search term */
  const [search, setSearch] = useState(() =>
    loadItem(`${storageKey}-search`, defaultSearch)
  );
  /** @type {Array} Currently selected filters */
  const [selectedFilters, setSelectedFilters] = useState(() =>
    loadItem(`${storageKey}-filters`, defaultFilters)
  );
  useEffect(() => {
    setSearch(loadItem(`${storageKey}-search`, defaultSearch));
    setSelectedFilters(loadItem(`${storageKey}-filters`, defaultFilters));
  }, [storageKey]);
  
  useEffect(() => {
    saveItem(`${storageKey}-search`, search);
  }, [search, storageKey]);

  useEffect(() => {
    saveItem(`${storageKey}-filters`, selectedFilters);
  }, [selectedFilters, storageKey]);

  /**
   * Clears all selected filters and resets to empty array
   */
  const clearFilters = () => setSelectedFilters([]);

  return {
    /** @type {string} Current search term */
    search,
    /** @type {Function} Setter for search term */
    setSearch,
    /** @type {Array} Currently selected filters */
    selectedFilters,
    /** @type {Function} Setter for selected filters */
    setSelectedFilters,
    /** @type {Function} Clear all filters */
    clearFilters,
  };
}
